import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon, FunnelIcon, TrashIcon, CheckIcon, PlusIcon } from '@heroicons/react/24/outline';

const ColumnFilter = ({ 
  column, 
  dataType, 
  onFilter, 
  currentFilter, 
  data = [],
  isActive = false,
  formatValue = null // New prop for formatting display values
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [rules, setRules] = useState([]);
  const [filterValue, setFilterValue] = useState('');
  const [selectedValues, setSelectedValues] = useState([]);
  const [filterOperator, setFilterOperator] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [booleanValue, setBooleanValue] = useState('true');
  const dropdownRef = useRef(null);

  const getDefaultOperator = () => (
    dataType === 'number_range'
      ? 'between'
      : dataType === 'boolean'
        ? 'equals'
        : dataType === 'date'
          ? 'equals'
          : 'contains'
  );

  const normalizeIncomingRules = (incomingFilter) => {
    if (!incomingFilter) return [];
    if (Array.isArray(incomingFilter.rules)) return incomingFilter.rules;
    return [incomingFilter];
  };

  // Sync state with currentFilter when opened
  useEffect(() => {
    if (showDropdown) {
      const incomingRules = normalizeIncomingRules(currentFilter);
      setRules(incomingRules);
      setFilterValue(currentFilter?.value || '');
      setSelectedValues(Array.isArray(currentFilter?.values) ? currentFilter.values : []);
      setFilterOperator(currentFilter?.operator || getDefaultOperator());
      setDateFrom(currentFilter?.dateFrom || '');
      setDateTo(currentFilter?.dateTo || '');
      setMinValue(currentFilter?.minValue || '');
      setMaxValue(currentFilter?.maxValue || '');
      setBooleanValue(currentFilter?.booleanValue || 'true');
    }
  }, [showDropdown, currentFilter, dataType]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowDropdown(false);
    };
    if (showDropdown) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when sidebar is open
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [showDropdown]);

  const formatRuleLabel = (rule) => {
    if (!rule) return '';
    if (rule.dataType === 'boolean') {
      return `${rule.operator} ${rule.booleanValue ? 'Yes' : 'No'}`;
    }
    if (rule.dataType === 'date') {
      if (rule.operator === 'between') return `${rule.operator}: ${rule.dateFrom} to ${rule.dateTo}`;
      return `${rule.operator}: ${rule.dateFrom}`;
    }
    if (rule.dataType === 'number_range') {
      if (rule.operator === 'between') return `${rule.operator}: ${rule.minValue} to ${rule.maxValue}`;
      return `${rule.operator}: ${rule.value}`;
    }
    if (Array.isArray(rule.values) && rule.values.length > 0) {
      return `${rule.operator}: ${rule.values.join(', ')}`;
    }
    return `${rule.operator}: ${rule.value}`;
  };

  // Get unique values for dropdown filters
  const getUniqueValues = () => {
    const values = data.map(item => item[column]).filter(val => val != null && val !== '');
    return [...new Set(values.map(v => formatValue ? formatValue(v) : String(v)))].sort((a, b) => a.localeCompare(b));
  };

  const getOperatorOptions = () => {
    switch (dataType) {
      case 'text':
        return [
          { value: 'contains', label: 'Contains' },
          { value: 'equals', label: 'Equals' },
          { value: 'starts_with', label: 'Starts with' },
          { value: 'ends_with', label: 'Ends with' },
          { value: 'not_contains', label: 'Does not contain' }
        ];
      case 'number':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'greater_than', label: 'Greater than' },
          { value: 'less_than', label: 'Less than' },
          { value: 'between', label: 'Between' }
        ];
      case 'number_range':
        return [
          { value: 'between', label: 'Between' },
          { value: 'equals', label: 'Equals' },
          { value: 'greater_than', label: 'Greater than' },
          { value: 'less_than', label: 'Less than' }
        ];
      case 'boolean':
        return [
          { value: 'equals', label: 'Is' }
        ];
      case 'date':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'before', label: 'Before' },
          { value: 'after', label: 'After' },
          { value: 'between', label: 'Between' }
        ];
      default:
        return [{ value: 'contains', label: 'Contains' }];
    }
  };

  const buildDraftRule = () => {
    let filter = null;

    if (dataType === 'date') {
      if (filterOperator === 'between') {
        if (dateFrom && dateTo) {
          filter = { column, operator: filterOperator, dateFrom, dateTo, dataType };
        }
      } else if (dateFrom) {
        filter = { column, operator: filterOperator, dateFrom, dataType };
      }
    } else if (dataType === 'number_range') {
      if (filterOperator === 'between' && minValue && maxValue) {
        filter = { column, operator: filterOperator, minValue: parseFloat(minValue), maxValue: parseFloat(maxValue), dataType };
      } else if (filterOperator !== 'between' && filterValue.trim()) {
        filter = { column, operator: filterOperator, value: parseFloat(filterValue.trim()), dataType };
      }
    } else if (dataType === 'boolean') {
      filter = { column, operator: filterOperator, booleanValue: booleanValue === 'true', dataType };
    } else {
      if (selectedValues.length > 0) {
        filter = { column, operator: filterOperator, values: selectedValues, dataType };
      } else if (filterValue.trim()) {
        filter = { column, operator: filterOperator, value: filterValue.trim(), dataType };
      }
    }

    return filter;
  };

  const addCondition = () => {
    const nextRule = buildDraftRule();
    if (!nextRule) return;
    setRules(prev => {
      const exists = prev.some(rule => JSON.stringify(rule) === JSON.stringify(nextRule));
      if (exists) return prev;
      return [...prev, nextRule];
    });
    setFilterValue('');
    setSelectedValues([]);
    setDateFrom('');
    setDateTo('');
    setMinValue('');
    setMaxValue('');
  };

  const applyFilter = () => {
    const draftRule = buildDraftRule();
    let finalRules = [...rules];

    if (draftRule) {
      const exists = rules.some(rule => JSON.stringify(rule) === JSON.stringify(draftRule));
      if (!exists) finalRules.push(draftRule);
    }

    if (!finalRules.length) {
      onFilter(null);
    } else {
      onFilter({ column, dataType, rules: finalRules });
    }
    setShowDropdown(false);
  };

  const clearFilter = () => {
    setRules([]);
    setFilterValue('');
    setSelectedValues([]);
    setDateFrom('');
    setDateTo('');
    setMinValue('');
    setMaxValue('');
    setBooleanValue('true');
    onFilter(null);
    setShowDropdown(false);
  };

  const renderFilterInput = () => {
    const uniqueValues = getUniqueValues();
    const searchTerm = filterValue.trim().toLowerCase();
    const matchingSuggestions = dataType === 'text'
      ? uniqueValues
          .filter(val => !searchTerm || val.toLowerCase().includes(searchTerm))
          .slice(0, 100)
      : [];

    if (dataType === 'date') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {filterOperator === 'between' ? 'From Date' : 'Date'}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
            />
          </div>
          {filterOperator === 'between' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              />
            </div>
          )}
        </div>
      );
    }

    if (dataType === 'number_range') {
      if (filterOperator === 'between') {
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">From Amount</label>
              <input
                type="number"
                step="0.01"
                value={minValue}
                onChange={(e) => setMinValue(e.target.value)}
                placeholder="Min amount"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">To Amount</label>
              <input
                type="number"
                step="0.01"
                value={maxValue}
                onChange={(e) => setMaxValue(e.target.value)}
                placeholder="Max amount"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>
        );
      } else {
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Amount</label>
            <input
              type="number"
              step="0.01"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder="Enter amount"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              onKeyDown={(e) => e.key === 'Enter' && applyFilter()}
            />
          </div>
        );
      }
    }

    if (dataType === 'boolean') {
      const getBooleanOptions = () => {
        if (column === 'request_inward') return [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];
        if (column === 'invoice_file') return [{ value: 'true', label: 'Has Invoice' }, { value: 'false', label: 'No Invoice' }];
        return [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];
      };

      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
          <select
            value={booleanValue}
            onChange={(e) => setBooleanValue(e.target.value)}
            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
          >
            {getBooleanOptions().map(option => (
              <option key={option.value} value={option.value} className="text-gray-900">{option.label}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Filter Value</label>
          <input
            type="text"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            placeholder={`Search ${column}...`}
            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
            onKeyDown={(e) => e.key === 'Enter' && addCondition()}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Suggestions</label>
          <div className="border border-gray-100 rounded-2xl max-h-60 overflow-y-auto p-3 space-y-2 bg-gray-50/50">
            {matchingSuggestions.length === 0 ? (
              <div className="text-sm text-gray-400 py-4 text-center italic">No matching values found</div>
            ) : (
              matchingSuggestions.map(value => (
                <label key={value} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white hover:shadow-sm cursor-pointer transition-all group">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(value)}
                    onChange={(e) => {
                      setSelectedValues(prev => (
                        e.target.checked ? [...prev, value] : prev.filter(v => v !== value)
                      ));
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900 truncate" title={value}>{value}</span>
                </label>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        onClick={() => setShowDropdown(true)}
        className={`p-1.5 rounded-lg transition-all ${isActive ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:bg-gray-100'}`}
        title={`Filter ${column}`}
      >
        <FunnelIcon className="w-4 h-4" />
      </button>

      {/* Sidebar Overlay - Rendered via Portal to avoid z-index/clipping issues in sticky table headers */}
      {showDropdown && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10000] overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300" 
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Sidebar Panel */}
          <div className="absolute inset-y-0 right-0 w-full max-w-sm bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out">
            <div className="p-6 flex flex-col h-full">
              {/* Header */}
              <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-xl">
                    <FunnelIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Filter Column</h3>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">{column.replace(/_/g, ' ')}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowDropdown(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Sidebar Content */}
              <div className="flex-1 overflow-y-auto space-y-8 pr-2 custom-scrollbar">
                {/* Operator Selector */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Matching Mode</label>
                  <div className="grid grid-cols-1 gap-2">
                    {getOperatorOptions().map(option => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setFilterOperator(option.value);
                          setSelectedValues([]);
                        }}
                        className={`px-4 py-3 text-sm font-medium rounded-xl text-left border transition-all ${
                          filterOperator === option.value 
                            ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm' 
                            : 'bg-white border-gray-100 text-gray-600 hover:border-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Main Filter Input */}
                <div className="bg-slate-50/50 p-5 rounded-2xl border border-slate-100">
                  {renderFilterInput()}
                </div>

                {/* Active Rules List */}
                {rules.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-gray-700">Applied Conditions</h4>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full">{rules.length}</span>
                    </div>
                    <div className="space-y-2">
                      {rules.map((rule, idx) => (
                        <div key={`${rule.operator}-${idx}`} className="flex items-center justify-between gap-3 bg-white p-3 rounded-xl border border-gray-100 shadow-sm group">
                          <span className="text-sm text-gray-700 font-medium truncate" title={formatRuleLabel(rule)}>
                            {formatRuleLabel(rule)}
                          </span>
                          <button
                            onClick={() => setRules(prev => prev.filter((_, i) => i !== idx))}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            title="Remove condition"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar Footer */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex gap-3 mb-2">
                    <button
                      onClick={addCondition}
                      disabled={dataType === 'date' && filterOperator === 'between' ? (!dateFrom || !dateTo) : false}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold bg-white border border-blue-200 text-blue-600 rounded-xl hover:bg-blue-50 transition-all disabled:opacity-50"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add to List
                    </button>
                    <button
                      onClick={clearFilter}
                      className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold bg-gray-50 text-gray-600 rounded-xl hover:bg-gray-100 transition-all"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Clear
                    </button>
                  </div>
                  <button
                    onClick={applyFilter}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 text-sm font-bold bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all transform active:scale-95"
                  >
                    <CheckIcon className="w-5 h-5" />
                    Apply All Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ColumnFilter;
